import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { styled } from "@mui/material/styles";
import { Phone, TrendingUp, People } from "@mui/icons-material";

// Animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const glow = keyframes`
  0%, 100% {
    text-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(76, 175, 80, 0.6);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

// Helper function to format numbers with K, M, B suffixes
const formatNumber = (num: number): string => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Animated Counter Component
const AnimatedCounter: React.FC<{ value: number; isLoading: boolean }> = ({ value, isLoading }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [formattedValue, setFormattedValue] = useState('0');

  useEffect(() => {
    if (!isLoading && value) {
      setIsAnimating(true);
      let start = 0;
      const end = value;
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setDisplayValue(end);
          setFormattedValue(formatNumber(end));
          clearInterval(timer);
          setTimeout(() => setIsAnimating(false), 500);
        } else {
          const currentValue = Math.floor(start);
          setDisplayValue(currentValue);
          setFormattedValue(formatNumber(currentValue));
        }
      }, 16);
      
      return () => clearInterval(timer);
    } else if (!isLoading && !value) {
      setDisplayValue(0);
      setFormattedValue('0');
    }
  }, [value, isLoading]);

  return (
    <Typography
      sx={{
        fontSize: { xs: '1.3rem', sm: '1.5rem' },
        fontWeight: 800,
       // background: 'linear-gradient(135deg, #fff, #4CAF50, #fff)',
       background:'white',
        backgroundSize: '200% auto',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animation: isAnimating ? `${shimmer} 2s linear infinite, ${glow} 1.5s ease-in-out infinite` : 'none',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {isLoading ? '---' : formattedValue}
      {!isLoading && !isAnimating && (
        <TrendingUp 
          sx={{ 
            fontSize: '1rem', 
            color: '#fff',
            animation: `${float} 2s ease-in-out infinite`
          }} 
        />
      )}
    </Typography>
  );
};

// Rest of your styled components remain the same...
const SocialIconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  marginTop: theme.spacing(2),
}));

const SocialIconLink = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    transform: "translateY(-5px)",
    animation: "dance 0.6s ease-in-out infinite alternate",
  },
  "@keyframes dance": {
    "0%": { transform: "translateY(0) rotate(0deg)" },
    "25%": { transform: "translateY(-4px) rotate(-2deg)" },
    "50%": { transform: "translateY(-6px) rotate(0deg)" },
    "75%": { transform: "translateY(-4px) rotate(2deg)" },
    "100%": { transform: "translateY(-8px) rotate(0deg)" },
  },
}));

const SocialIcon = styled("img")({
  width: "30px",
  height: "30px",
  objectFit: "contain",
  borderRadius: "50%",
});

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <Typography fontWeight="bold" fontSize={{ xs: "15px", sm: "16px" }} mb={0.5}>
      {children}
    </Typography>
    <Box sx={{ width: "70%", borderBottom: "2px solid #fff", mb: 1 }} />
  </>
);

const InfoItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    fontSize={{ xs: "13px", sm: "14px" }}
    sx={{ mb: 0.5, display: "flex", alignItems: "flex-start", lineHeight: 1.3 }}
  >
    <span style={{ marginRight: 6, flexShrink: 0 }}>{">>"}</span>
    {children}
  </Typography>
);

const ContactItem: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box display="flex" alignItems="flex-start" mb={1}>
      {icon}
      <Typography
        fontSize={{ xs: "13px", sm: "14px" }}
        sx={{
          lineHeight: 1.3,
          wordBreak: "break-word",
          paddingRight: isMobile ? "10px" : "0",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

const Footer = () => {
  const [visitorCount, setVisitorCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(true);
  const [countError, setCountError] = useState(false);

  useEffect(() => {
    const fetchVisitorCount = async () => {
      try {
        const response = await fetch('https://gcrs.co.in/apis/visitors/', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result && typeof result.count === 'number') {
          setVisitorCount(result.count);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Failed to fetch visitor count:', error);
        setCountError(true);
        setVisitorCount(10414);
      } finally {
        setCountLoading(false);
      }
    };

    fetchVisitorCount();
  }, []);

  const informationItems = [
    "CIN No: U74930AP2014PTC095735",
    "GSTIN: 37AAFCG8137J1Z9",
  ];

  const importantLinks = [
    { text: "Terms and Conditions", url: "/" },
    { text: "Privacy Policy", url: "/OurPrivacyPolicy" },
    { text: "Careers", url: "/Careers" },
  ];

  const socialLinks = [
    {
      url: "https://instagram.com/gcrs_2014",
      icon: "https://cdn-icons-png.flaticon.com/512/2111/2111463.png",
      alt: "Instagram",
    },
    {
      url: "https://www.linkedin.com/company/geo-climate-risk-solutions-pvt-ltd/",
      icon: "https://cdn-icons-png.flaticon.com/512/3536/3536505.png",
      alt: "LinkedIn",
    },
    {
      url: "https://www.youtube.com/channel/UC3pBDmo664dV8QAjYsSRpfA",
      icon: "https://cdn-icons-png.flaticon.com/512/3670/3670147.png",
      alt: "YouTube",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#071220",
        color: "#fff",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 2, md: 2 },
        fontFamily: "Calibri",
        overflow: "hidden",
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 2, md: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <img
              src="https://d357a0xuzx4dll.cloudfront.net/gcrs-website/gcrs_images/footer_icons/gcrslogo.svg"
              alt="GCRS Logo"
              style={{
                maxHeight: 50,
                width: "auto",
                maxWidth: "100%",
                marginBottom: "8px",
              }}
            />
            <Typography fontWeight="bold" fontSize={{ xs: "14px", sm: "15px" }} mb={0.5}>
              GEO CLIMATE RISK SOLUTIONS PVT LTD
            </Typography>
            <Typography fontStyle="italic" fontSize={{ xs: "12px", sm: "13px" }} mb={1}>
              ....for sustainability
            </Typography>
            <Typography fontSize={{ xs: "13px", sm: "14px" }} sx={{ lineHeight: 1.4, mb: 1 }}>
              GCRS is a solution provider, consultancy, and advisory services
              firm that focuses on environmental and sustainability challenges.
            </Typography>

            <SocialIconContainer sx={{ mt: 1 }}>
              {socialLinks.map((social, index) => (
                <SocialIconLink
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.alt}
                >
                  <SocialIcon src={social.icon} alt={social.alt} />
                </SocialIconLink>
              ))}
            </SocialIconContainer>
            {/* Animated Visitor Counter with K/M/B formatting */}
            <Box
              sx={{
                my: 2,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <AnimatedCounter 
                    value={visitorCount || 0} 
                    isLoading={countLoading} 
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', sm: '16px' },
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontWeight: 500,
                    }}
                  >
                    visitors worldwide
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
        
        {/* Rest of your grid items remain the same */}
        <Grid item xs={12} sm={6} md={2.5}>
          <SectionTitle>Information</SectionTitle>
          {informationItems.map((info, i) => (
            <InfoItem key={i}>{info}</InfoItem>
          ))}
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.5}>
          <SectionTitle>Important Links</SectionTitle>
          {importantLinks.map((link, i) => (
            <InfoItem key={i}>
              <Link href={link.url} color="inherit" underline="hover">
                {link.text}
              </Link>
            </InfoItem>
          ))}
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <SectionTitle>Contact Us</SectionTitle>
          <ContactItem icon={<Phone fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            +91 98107 08901
          </ContactItem>
          <ContactItem icon={<MailOutlineIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            business@gcrs.co.in
          </ContactItem>
          <ContactItem icon={<MailOutlineIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            info@gcrs.co.in
          </ContactItem>
          <ContactItem icon={<LocationOnIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            Registered Office: Flat No.: 27-B-34, Vuda Row Villa, Revenue
            ward-6, Sagar Nagar, Rushikonda APIIC, Visakhapatnam, Andhra
            Pradesh, 530045
          </ContactItem>
          <ContactItem icon={<LocationOnIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            Innovation Valley, Hill-3, IT SEZ, Madhurawada, Visakhapatnam - 530048
          </ContactItem>
          <ContactItem icon={<LocationOnIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            WeWork Rajapushpa Summit, SY. 130P & 115/1P, Nanakramguda Rd, Ranga Reddy Dist., 
            Financial District, Manikonda Jagir, Telangana 500032
          </ContactItem>
          <ContactItem icon={<LocationOnIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
            TP-125, First floor, Technopark@iitk, Phase-I Building, R&D Centre,
            Indian Institute of Technology (IIT) Kanpur, Uttar Pradesh – 208016
          </ContactItem>
            <ContactItem icon={<LocationOnIcon fontSize="small" sx={{ mr: 1, mt: "1px", flexShrink: 0 }} />}>
           Pinnacle space, Second floor, 50-51, Regal Building, Parliament Street, Connaught Place, New Delhi, 110001
          </ContactItem>
        </Grid>
      </Grid>

      <Box textAlign="center" sx={{ mt: 2 }}>
        <Typography fontSize={{ xs: "12px", sm: "13px" }}>
          Copyright © 2025 Geo Climate Risk Solutions Pvt. Ltd.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;